import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getArchetype,
  listArchetypes,
  listMetricTemplates,
  publishArchetype,
  retireArchetype,
  saveArchetypeDraft,
  type ArchetypeDetail,
  type ArchetypeVersion,
  type MetricTemplate,
  type PublishArchetypeParams,
  type SaveArchetypeDraftParams,
} from "@/lib/admin-api";

export const ARCHETYPES_KEY = ["archetypes"] as const;

export function archetypeKey(archetypeId: string) {
  return ["archetypes", "detail", archetypeId] as const;
}

const METRIC_TEMPLATE_KEY = ["metric-templates", "player"] as const;

/** The template the player archetypes weight. */
export const PLAYER_TEMPLATE_ID = "player-outfield";

export function useArchetypes({ enabled = true }: { enabled?: boolean } = {}) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ARCHETYPES_KEY,
    queryFn: listArchetypes,
    enabled,
  });
  return { archetypes: data?.archetypes ?? [], loading: isPending, failed: isError, error };
}

/** One archetype with its history. Disabled for a new archetype (no id yet). */
export function useArchetype(archetypeId: string | undefined) {
  return useQuery({
    queryKey: archetypeKey(archetypeId ?? ""),
    queryFn: () => getArchetype(archetypeId ?? ""),
    enabled: Boolean(archetypeId),
    // The editor seeds its form and revision from this once, so a refetch
    // (after a write, or a Reload) never moves the revision under an open form.
  });
}

/**
 * The player metric template, for the metric picker. Empty while
 * game-service's profile reads are off (503): the editor still shows the
 * stored weights with their stored labels.
 */
export function usePlayerMetricTemplate() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: METRIC_TEMPLATE_KEY,
    queryFn: () => listMetricTemplates("player"),
    staleTime: 10 * 60 * 1000,
  });
  const template: MetricTemplate | undefined =
    data?.templates.find((t) => t.templateId === PLAYER_TEMPLATE_ID) ?? data?.templates[0];
  return { template, loading: isPending, failed: isError, error };
}

/** Folds one written version into the cached detail, so the form reseeds without a refetch. */
function mergeVersion(
  detail: ArchetypeDetail | undefined,
  version: ArchetypeVersion,
): ArchetypeDetail {
  if (!detail) {
    // A new archetype: its first draft is its whole history.
    return {
      archetypeId: version.archetypeId,
      draft: version,
      published: null,
      versions: [version],
    };
  }
  const versions = detail.versions.filter((v) => v.version !== version.version);
  versions.push(version);
  versions.sort((a, b) => b.version - a.version);
  return {
    ...detail,
    draft: version.status === "draft" ? version : detail.draft,
    versions,
  };
}

export function useSaveArchetypeDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: SaveArchetypeDraftParams) => saveArchetypeDraft(params),
    onSuccess: ({ draft }) => {
      queryClient.setQueryData<ArchetypeDetail>(archetypeKey(draft.archetypeId), (detail) =>
        mergeVersion(detail, draft),
      );
      // Authorship names come from the BFF's user lookup, which the save
      // response does not carry, so the detail is refetched as well.
      void queryClient.invalidateQueries({ queryKey: archetypeKey(draft.archetypeId) });
      void queryClient.invalidateQueries({ queryKey: ARCHETYPES_KEY, exact: true });
    },
  });
}

export function usePublishArchetype() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: PublishArchetypeParams) => publishArchetype(params),
    onSettled: (_data, _error, params) => {
      void queryClient.invalidateQueries({ queryKey: archetypeKey(params.archetypeId) });
      void queryClient.invalidateQueries({ queryKey: ARCHETYPES_KEY, exact: true });
    },
  });
}

export function useRetireArchetype() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (archetypeId: string) => retireArchetype(archetypeId),
    onSettled: (_data, _error, archetypeId) => {
      void queryClient.invalidateQueries({ queryKey: archetypeKey(archetypeId) });
      void queryClient.invalidateQueries({ queryKey: ARCHETYPES_KEY, exact: true });
    },
  });
}
